import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from "@/hooks/useNotify";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { logAuthTransition } from "@/utils/authStateMonitor";

export const useCompanyCreation = () => {
  const { user, profile, refreshProfile, setProfile } = useAuth();
  const [companyName, setCompanyName] = useState("");
  const [website, setWebsite] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [suburb, setSuburb] = useState("");
  const [city, setCity] = useState("");
  const [provinceState, setProvinceState] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const notify = useNotify();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.id) {
      notify.error("Authentication error — You must be logged in to create a company.");
      return;
    }
    
    setIsLoading(true);
    
    try {
      const email = user.email;
      
      // Clear any existing company association first (for deactivated users)
      const { error: clearError } = await supabase
        .from('profiles')
        .update({
          company_id: null,
          status: 'pending',
          updated_at: new Date().toISOString()
        })
        .eq("id", user.id);
        
      if (clearError) throw clearError;
      
      // Create company
      const { data: company, error: companyError } = await supabase
        .from('companies')
        .insert({
          name: companyName,
          email: email,
          website: website,
          street_address: streetAddress,
          suburb: suburb,
          city: city,
          province_state: provinceState,
          postal_code: postalCode,
          logo_url: logoUrl,
          created_by: user.id
        })
        .select()
        .single();
        
      if (companyError) throw companyError;
      
      // Add user as company member with admin role
      const { error: memberError } = await supabase
        .from('company_members')
        .insert({
          company_id: company.id,
          user_id: user.id,
          role: 'admin'
        });
        
      if (memberError) throw memberError;
      
      // Update user's profile to link to new company and reactivate user
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          company_id: company.id,
          status: 'active',
          updated_at: new Date().toISOString()
        })
        .eq("id", user.id);
      
      if (profileError) throw profileError;

      // Immediately update AuthContext profile state to prevent race condition
      if (profile) {
        setProfile({
          ...profile,
          company_id: company.id,
          status: 'active'
        });
      }

      // Log company creation
      logAuthTransition(
        'company_created',
        user.id,
        { companyId: company.id, companyName },
        'standalone_company_creation'
      );

      // Refresh profile data to sync with AuthContext
      if (refreshProfile) {
        await refreshProfile();
      }

      notify.success("Company created successfully — Your new company is ready to use.");
      
      navigate("/dashboard", { replace: true });
    } catch (error: any) {
      console.error("Company creation error:", {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code,
        status: error.status,
        fullError: error
      });
      
      notify.error(`Company creation failed — ${error.message || "An error occurred. Please try again."}`);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    companyName,
    setCompanyName,
    website,
    setWebsite,
    streetAddress,
    setStreetAddress,
    suburb,
    setSuburb,
    city,
    setCity,
    provinceState,
    setProvinceState,
    postalCode,
    setPostalCode,
    logoUrl,
    setLogoUrl,
    isLoading,
    handleSubmit,
  };
};
